/**
 * Decorator for $q service
 */

import { indexIn } from 'app/helpers/entity'
import { has } from 'app/helpers/object'

/**
 * $q decorator <br>
 * Add custom methods to $q service
 *
 * @class QDecorator
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc decorator
 * @name mwApp/common.service:QDecorator
 */
/* @ngInject */
export default function QDecorator ($delegate) {
  //

  /**
   * Decorating method, add methods to given promise
   *
   * @private
   * @memberof mwApp/common.service:notifySvc~
   * @param  {Promise} promise
   * @return {Promise} promise
   */
  function decoratePromise (promise) {
    const then = promise.then

    // overwrite promise.then() method
    // $q's custom methods .catch() and .finally() are implemented by using .then themselves, so they're covered too
    promise.then = function () {
      return decoratePromise(then.apply(this, arguments))
    }

    /**
     * Get 'data' field from promise result
     *
     * @return {Promise} promise
     */
    promise.data = function () {
      return (
        promise
          .then(
            data =>
              data && data.data !== undefined // return promise result for next promise in chain, if any
                ? data.data
                : null
          )
      )
    }

    /**
     * Remove values from promise result
     *
     * @param  {*} a - smth to be removed
     * @return {Promise} promise
     */
    promise.except = function (a) {
      return (
        !a
          ? promise
          : promise
            .then(
              data => {
                // if promise result is arrays -> remove given argument from array
                if (Array.isArray(data)) {
                  removeFromArray(data, a)
                } else

                // if promise result is object -> remove given argument from object
                if (data && typeof data === 'object') {
                  removeFromObject(data, a)
                }

                // return promise result for next promise in chain, if any
                return data
              }
            )
      )
    }

    /**
     * Assigns the value given to .then on promise resolution to the given object under the given name
     *
     * @param  {Object} obj    - object
     * @return {Promise} promise
     */
    promise.to = function (obj) {
      return (
        !obj || typeof obj !== 'object'
          ? promise
          : promise
            .then(
              data => {
                // if object and promise result are arrays -> concat arrays
                if (Array.isArray(obj) && Array.isArray(data)) {
                  // concat arrays without creating new object
                  Array.prototype.push.apply(obj, data)
                } else

                // if promise result is object -> extend given object with it
                if (data && typeof data === 'object') {
                  angular.extend(obj, data)
                }

                // return promise result for next promise in chain, if any
                return data
              }
            )
      )
    }

    return promise
  }

  // wrap and overwrite $q's deferred object methods

  const defer = $delegate.defer
  const when = $delegate.when
  const resolve = $delegate.resolve
  const reject = $delegate.reject
  const all = $delegate.all

  $delegate.defer = function () {
    let deferred = defer()
    decoratePromise(deferred.promise)
    return deferred
  }

  $delegate.when = function () {
    return decoratePromise(when.apply(this, arguments))
  }

  // fix 1.6.x angular versions
  // https://github.com/angular/angular.js/commit/04cbe1e74f6d5cc5e0f12fce63ec8643fe306908#diff-748e0a1e1a7db3458d5f95d59d7e16c9
  $delegate.resolve = function () {
    return decoratePromise(resolve.apply(this, arguments))
  }

  $delegate.reject = function () {
    return decoratePromise(reject.apply(this, arguments))
  }

  $delegate.all = function () {
    return decoratePromise(all.apply(this, arguments))
  }

  return $delegate

  /* === private helper functions === */

  /**
   * Remove entity from array
   *
   * @private
   * @memberof mwApp/common.service:QDecorator
   * @param {models.Entity[]} arr
   * @param {Number} entityId
   */
  function remove (arr, entityId) {
    let index = indexIn(arr)(entityId)
    if (index !== -1) {
      arr.splice(index, 1)
    }
  }

  /**
   * Remove items from array
   *
   * @private
   * @memberof mwApp/common.service:QDecorator
   * @param  {Object[]} srcArr - source array
   * @param  {*}        what   - what to remove
   */
  function removeFromArray (srcArr, what) {
    if (!srcArr || !what || !srcArr.length) {
      return
    }

    let i

    // 'what' is a filter function
    if (typeof what === 'function') {
      for (i = 0; i < srcArr.length; i++) {
        if (what(srcArr[i])) {
          srcArr.splice(i, 1)
          i--
        }
      }
    } else

    // 'what' is an array
    if (Array.isArray(what)) {
      // remove elements from source array one by one
      for (i = what.length; i--;) {
        remove(srcArr, what[i])
      }

    // 'what' is a single element
    } else {
      // remove single item from array
      remove(srcArr, what)
    }
  }

  /**
   * Remove fields from object
   *
   * @private
   * @memberof mwApp/common.service:QDecorator
   * @param  {Object[]} srcObj - source object
   * @param  {*}        what   - fields to remove
   */
  function removeFromObject (srcObj, what) {
    if (!srcObj || !what) {
      return
    }

    let i

    // 'what' is an array
    if (Array.isArray(what)) {
      // remove elements from source array one by one
      for (i = what.length; i--;) {
        removeFromObject(srcObj, what[i])
      }
    } else

    // 'what' is an string
    if (typeof what === 'string') {
      if (has(what)(srcObj)) {
        delete srcObj[what]
      }
    } else

    // 'what' is an object
    if (typeof what === 'object') {
      for (i in what) {
        let hasI = has(i)
        if (hasI(what) && hasI(srcObj)) {
          if (what[i] !== null && typeof what[i] === 'object' && srcObj[i] !== null && typeof srcObj[i] === 'object') {
            removeFromObject(srcObj[i], what[i])
          } else {
            delete srcObj[i]
          }
        }
      }
    }
  }
}
